"use client";

import { createContext, useContext } from "react";

import { isKnownWalletId, quirk } from "@/lib/wallet/solana/quirks";

import type { GlyphWalletMembershipSource } from "@/lib/wallet/glyph-wallet-membership";
import type { QuirkEntry, WalletId } from "@/lib/wallet/solana/types";

/** Privy 3.46's Solana `walletClientType`s that we have a wallet for. */
export type PrivySolanaClientType = NonNullable<QuirkEntry["privyClientType"]>;

/**
 * The client type the Glyph link records for the connected wallet. Phantom
 * on the Phantom-only path, which names no wallet; otherwise the quirk's,
 * and none for a wallet Privy does not list. Missing provider metadata does
 * not prevent linking a separately connected, signing-capable wallet.
 */
export function privyClientTypeOf(
  wallet: WalletId | undefined
): PrivySolanaClientType | undefined {
  if (wallet === undefined) return "phantom";
  return isKnownWalletId(wallet) ? quirk(wallet).privyClientType : undefined;
}

/**
 * Sign in with Glyph, in a Phantom build: an account (a name and a picture),
 * never a wallet. Nothing here reaches `WalletState`; Phantom stays the only
 * Solana wallet and RainbowKit the only EVM one.
 */

export type GlyphLoginMode = "oauth" | "existing-evm";

/** What Privy hands up once it has loaded. */
export interface GlyphSessionApi {
  ready: boolean;
  authenticated: boolean;
  privyUserId: string | null;
  /** Privy-confirmed external account-list membership, never wallet authority. */
  walletMembership?: GlyphWalletMembershipSource;
  /** A discarded mobile wallet URL cancels only the pre-mount login intent. */
  walletResumeBlocked?: boolean;
  login: (mode?: GlyphLoginMode) => void;
  logout: () => Promise<void>;
  /**
   * Both Privy tokens, or null when signed out. `fresh: false` reuses the
   * identity token Privy already holds while it is far from expiry, instead of
   * re-reading the user from Privy (a rate-limited call).
   */
  tokens: (options?: {
    fresh?: boolean;
  }) => Promise<{ identityToken: string; accessToken: string } | null>;
  /**
   * Adds an external Solana wallet to the Glyph account in Privy, so other
   * Yuga apps see it. Confirms the returned user and external wallet membership.
   * Off the money path: these addresses never choose a provider or recipient.
   */
  addSolanaWalletToGlyph: (
    address: string,
    signMessage: (message: Uint8Array) => Promise<Uint8Array>,
    /** Privy's name for the wallet that signs, as it records the link. */
    walletClientType?: PrivySolanaClientType,
    /** Reject a delayed request after the selected session/provider changes. */
    isCurrent?: () => boolean
  ) => Promise<void>;
}

/** The server's answer for this browser's proven wallet and this account. */
export type GlyphLink =
  | {
      state: "linked";
      wallet: string;
      username: string | null;
      pfp: string | null;
    }
  /** No Sign-In-With-Solana seat in this browser yet. */
  | { state: "not-proven" }
  /** Linked elsewhere; moving needs the holder's say and a fresh sign-in. */
  | { state: "elsewhere"; linked: string | null; wallet: string };

export interface GlyphAccount {
  /** Current authenticated identity, used only to scope local UI state. */
  accountId?: string | null;
  status: "signed-out" | "starting" | "signed-in";
  signIn: () => void;
  /** Native Glyph login/signup entry; never chooses an asset wallet. */
  signInWithEvm?: () => void;
  signOut: () => void;
  link: GlyphLink | undefined;
  linkError: string | null;
  linking: boolean;
  /** Proves the connected wallet afresh, then moves the link to it. */
  relink: () => void;
  /** Proves the connected wallet (one prompt), then links it. */
  proveAndLink: () => void;
  /**
   * Explicit Privy link for the connected external signer, never on a money path.
   * Provider metadata is optional; missing signing support disables the action.
   */
  addToGlyph?: () => void;
  addedToGlyph: boolean;
  /** True while `addedToGlyph` is still loading rather than known false. */
  addedToGlyphPending?: boolean;
  /** The current external wallet is being added to this Glyph account's list. */
  addingToGlyph?: boolean;
  /** The game seat plays under the Glyph name (no Solana wallet needed). */
  named: boolean;
  /** Server-verified game identity only; never an asset recipient or signer. */
  rankedWallet?: string | null;
  rankingPending?: boolean;
}

export const GlyphAccountContext = createContext<GlyphAccount | null>(null);

/** Null where the build offers no Glyph sign-in. */
export function useGlyphAccount(): GlyphAccount | null {
  return useContext(GlyphAccountContext);
}
